<template>
  <div class="exhibition-section">
    <div class="exhibition-section__wrapper" ref="wrapper">
      <div class="exhibition-section__content" ref="content">
        <slot></slot>
      </div>
      <div v-if="shimset === 'show'" 
           class="exhibition-section__shim" 
           ref="shim"></div>
    </div>
  </div>
</template>

<script lang="js">
import reflow from '../../js/util/reflow';
import createEasingFunction from '../../js/animation/createEasingFunction';

const fadeIn = createEasingFunction('ease-out');

export default  {
  name: 'ahc-exhibition-section',
  inject: {
    exhibition: {
      default: () => ({})
    }
  },
  props: {
    distance: {
      type: Number,
      default: 0,
    },
    shimset: {
      default: 'show'
    }
  },
  methods: {
    update() {
      if (this.$refs.shim && this.exhibition.pageYOffset >= this.pinOffsetStart && this.exhibition.pageYOffset <= this.pinOffsetEnd) {
        // "pin" the content as position: fixed so the previous section will appear to
        // slide over it
        if (!this.pinned) {
          this.$refs.content.style.position = 'fixed';
          this.$refs.content.style.top = 0;
          this.$refs.content.style.left = 0;
          this.$refs.content.style.transform = `translate3d(0, 0, 0)`;
          this.$refs.content.classList.add('exhibition-section__content--pinned');

          this.pinned = true;
        }
      } else {
        // The content is unpinned
        if (this.pinned) {
          this.$refs.content.style.position = this.originalStyles.position;
          this.$refs.content.style.top = this.originalStyles.top;
          this.$refs.content.style.left = this.originalStyles.left;
          this.$refs.content.classList.remove('exhibition-section__content--pinned');

          // at the end of the pin, translate the content by the extra pin "distance"
          // to create the illusion that it is scrolling away from its new position
          if (this.exhibition.pageYOffset >= this.pinOffsetEnd) {
            this.$refs.content.style.transform = `translate3d(0, ${this.distance}px, 0)`;
          } else {
            this.$refs.content.style.transform = `translate3d(0, 0, 0)`;
          }

          this.pinned = false;
        }
      }

      if (this.exhibition.pageYOffset >= this.fadeOffsetStart && this.exhibition.pageYOffset <= this.fadeOffsetEnd) {
        // the opacity of the "shim" is a function of the scroll position
        this.fadeInDone = false;
        const progress = ((this.exhibition.pageYOffset - this.fadeOffsetStart) / (this.fadeOffsetEnd - this.fadeOffsetStart));
        if (typeof(this.$refs.shim) == 'object'){
          this.$refs.shim.style.opacity = 1 - fadeIn(progress).toFixed(2);
        }
      } else {
        // the opacity transition is done
        if (!this.fadeInDone) {
          if (typeof(this.$refs.shim) == 'object'){
            this.$refs.shim.style.opacity = 0;
          }
          this.fadeInDone = true;
        }
      }
    },
    redraw() {
      this.pinned = false;
      this.fadeInDone = true;

      // restore the content's original inline styles and remove height
      // set on the wrapper so that we can measure the content's height again
      // force reflows so that the correct dimensions can be read on the same tick
      this.$refs.content.style.height = this.originalStyles.height;
      this.$refs.content.style.width = this.originalStyles.width;
      this.$refs.content.style.maxHeight = this.originalStyles.maxHeight;
      this.$refs.content.style.maxWidth = this.originalStyles.maxWidth;
      this.$refs.content.style.position = this.originalStyles.position;
      this.$refs.content.style.top = this.originalStyles.top;
      this.$refs.content.style.left = this.originalStyles.left;
      this.$refs.content.style.transform = this.originalStyles.transform;

      reflow(this.$refs.content);

      this.$refs.wrapper.style.height = '';
      this.$refs.wrapper.style.width = '';
      this.$refs.wrapper.style.maxWidth = '';
      this.$refs.wrapper.style.maxHeight = '';

      reflow(this.$refs.wrapper);

      // if it doesn't have shim in place, don't get style
      if (typeof(this.$refs.shim) == 'object'){
        this.$refs.shim.style.opacity = '';
      }

      // measure the content and set the height of the wrapper to the same amount
      // so that it can placehold the same amount of scroll distance
      let rect = this.$refs.content.getBoundingClientRect();

      this.$refs.content.style.height = rect.height + 'px';
      this.$refs.content.style.width = rect.width + 'px';
      this.$refs.content.style.maxHeight = rect.height + 'px';
      this.$refs.content.style.maxWidth = rect.width + 'px';

      // the wrapper may be taller than the content to allow the content to be
      // "pinned" for longer
      this.$refs.wrapper.style.height = rect.height + this.distance + 'px';
      this.$refs.wrapper.style.width = rect.width + 'px';
      this.$refs.wrapper.style.maxHeight = rect.height + this.distance + 'px';
      this.$refs.wrapper.style.maxWidth = rect.width + 'px';

      reflow(this.$refs.content);
      reflow(this.$refs.wrapper);
    },
    recalc() {
      const rect = this.$refs.wrapper.getBoundingClientRect();

      this.scrollOffsetTop = rect.top + this.exhibition.pageYOffset;

      // calculate the offsets at which to start / end the animations

      // the fade is between the top of the wrapper hitting the bottom of the viewport
      // and the top of the wrapper hitting the top of the viewport...
      this.fadeOffsetStart = rect.top + this.exhibition.pageYOffset - this.exhibition.windowHeight;
      this.fadeOffsetEnd = this.fadeOffsetStart + this.exhibition.windowHeight;

      // content is pinned during the fade and for an additional pixel distance
      this.pinOffsetStart = this.fadeOffsetStart;
      this.pinOffsetEnd = this.fadeOffsetEnd + this.distance;
    },
    init() {
      const computedStyles = window.getComputedStyle(this.$refs.content);

      // save original inline styles
      this.originalStyles = {
        width: this.$refs.content.style.width || '',
        height: this.$refs.content.style.height || '',
        maxWidth: this.$refs.content.style.maxWidth || '',
        maxHeight: this.$refs.content.style.maxHeight || '',
        top: this.$refs.content.style.top || '',
        left: this.$refs.content.style.left || '',
        position: this.$refs.content.style.position || '',
        transform: this.$refs.content.style.transform || ''
      };

      // the "wrapper" preserves the scroll height of the content when the
      // content is position: fixed but otherwise does not impact layout
      this.$refs.wrapper.style.position = 'relative';
      this.$refs.wrapper.style.overflow = 'visible';
      this.$refs.wrapper.style.float = 'none';
      this.$refs.wrapper.style.flexShrink = 1;
      this.$refs.wrapper.style.display = computedStyles.display;
      this.$refs.wrapper.style.margin = 0;
      this.$refs.wrapper.style.padding = 0;
      this.$refs.wrapper.style.top = 0;
      this.$refs.wrapper.style.bottom = 0;
      this.$refs.wrapper.style.left = 0;
      this.$refs.wrapper.style.right = 0;
    },
    destroy() {
      this.pinned = false;
      this.fadeInDone = true;

      this.$refs.content.style.height = this.originalStyles.height;
      this.$refs.content.style.width = this.originalStyles.width;
      this.$refs.content.style.maxHeight = this.originalStyles.maxHeight;
      this.$refs.content.style.maxWidth = this.originalStyles.maxWidth;
      this.$refs.content.style.position = this.originalStyles.position;
      this.$refs.content.style.top = this.originalStyles.top;
      this.$refs.content.style.left = this.originalStyles.left;
      this.$refs.content.style.transform = this.originalStyles.transform;
      this.$refs.content.classList.remove('exhibition-section__content--pinned');

      this.$refs.wrapper.removeAttribute('style');
      
      if (typeof(this.$refs.shim) == 'object'){ 
        this.$refs.shim.style.opacity = '';
      }
    },
    onFocusIn(e) {
      const wrapper = e.target && e.target.closest('.exhibition-section__wrapper');
      const container = e.target && e.target.closest('.exhibition-section__content');

      // while a section is "pinned" it appears at the top of the viewport even though another section is
      // scrolling over it. Programmatically scroll enough to reveal an element when it takes focus
      if (wrapper && container && container.classList.contains('exhibition-section__content--pinned')) {
        this.$nextTick(() => {
          window.scrollTo(0, wrapper.getBoundingClientRect().top + window.pageYOffset);
        });
      }
    }
  },
  mounted() {

    if (this.exhibition.registerSection) {
      this.exhibition.registerSection(this);
    }

    this.$el.addEventListener('focusin', this.onFocusIn, false);
  },
  beforeDestroy() {
    this.$el.removeEventListener('focusin', this.onFocusIn, false);
  }
}
</script>
